import axios from 'axios';

const API_BASE_URL = '/api';

// 创建axios实例并添加请求拦截器
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
});

// 添加请求拦截器，自动添加Authorization头
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    const currentTenant = localStorage.getItem('currentTenant') || 'default';
    config.headers['X-Tenant-Code'] = currentTenant;
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 测试套件接口
export interface TestAgentSuite {
  id?: number;
  name: string;
  description?: string;
  tenant_code?: string;
  deleted_at?: string;
  created_at?: string;
  updated_at?: string;
}

// 对话接口
export interface TestAgentTurn {
  id?: number;
  suite_id: number;
  sequence: number;
  question: string;
  user_name?: string;
  user_code?: string;
  robot_name?: string;
  robot_code?: string;
  created_at?: string;
  expected_answers?: ExpectedAnswer[];
}

// 预期答案接口
export interface ExpectedAnswer {
  id?: number;
  turn_id: number;
  step_order: number;
  content: string;
  comparison_method: 'exact' | 'keyword' | 'custom';
  comparison_keywords?: string;
  comparison_custom?: string;
  created_at?: string;
}

// 测试执行记录接口
export interface TestAgentExecution {
  id?: number;
  suite_id: number;
  conversation_id?: string;
  status: 'running' | 'completed' | 'failed';
  started_at?: string;
  completed_at?: string;
}

// 测试结果接口
export interface TestAgentResult {
  id?: number;
  execution_id: number;
  turn_id: number;
  expected_answer_id: number;
  actual_content: string;
  comparison_method: 'exact' | 'keyword' | 'custom';
  match_status: 'matched' | 'mismatched' | 'partial';
  match_score?: number;
  diff_details?: string;
  created_at?: string;
  // 关联信息
  sequence?: number;
  question?: string;
  step_order?: number;
  expected_content?: string;
}

// 统计信息接口
export interface TestStatistics {
  total_results: number;
  matched_count: number;
  mismatched_count: number;
  partial_count: number;
  average_score: number;
}

export class TestAgentService {
  // ==================== 测试套件 API ====================

  // 获取测试套件列表
  async getTestAgentSuites(tenantCode: string, includeDeleted = false): Promise<TestAgentSuite[]> {
    const response = await api.get(
      `/${tenantCode}/test-agent/suites`,
      {
        params: { include_deleted: includeDeleted }
      }
    );
    return response.data.data;
  }

  // 获取测试套件详情
  async getTestAgentSuiteDetails(tenantCode: string, id: number): Promise<TestAgentSuite & { session_turns: TestAgentTurn[] }> {
    const response = await api.get(
      `/${tenantCode}/test-agent/suites/${id}`
    );
    return response.data.data;
  }

  // 创建测试套件
  async createTestAgentSuite(tenantCode: string, suite: Omit<TestAgentSuite, 'id' | 'created_at' | 'updated_at' | 'deleted_at' | 'tenant_code'>): Promise<TestAgentSuite> {
    const response = await api.post(
      `/${tenantCode}/test-agent/suites`,
      suite
    );
    return response.data.data;
  }

  // 更新测试套件
  async updateTestAgentSuite(tenantCode: string, id: number, updates: Partial<TestAgentSuite>): Promise<void> {
    await api.put(
      `/${tenantCode}/test-agent/suites/${id}`,
      updates
    );
  }

  // 删除测试套件（软删除）
  async deleteTestAgentSuite(tenantCode: string, id: number): Promise<void> {
    await api.delete(
      `/${tenantCode}/test-agent/suites/${id}`
    );
  }

  // 恢复测试套件
  async restoreTestAgentSuite(tenantCode: string, id: number): Promise<void> {
    await api.post(
      `/${tenantCode}/test-agent/suites/${id}/restore`
    );
  }

  // 获取回收站
  async getDeletedTestAgentSuites(tenantCode: string): Promise<TestAgentSuite[]> {
    const response = await api.get(
      `/${tenantCode}/test-agent/trash`
    );
    return response.data.data;
  }

  // 永久删除测试套件
  async permanentlyDeleteTestAgentSuite(tenantCode: string, id: number): Promise<void> {
    await api.delete(
      `/${tenantCode}/test-agent/suites/${id}/permanent`
    );
  }

  // ==================== 对话 API ====================

  // 创建对话
  async createTestAgentTurn(tenantCode: string, conversation: Omit<TestAgentTurn, 'id'>): Promise<TestAgentTurn> {
    const response = await api.post(
      `/${tenantCode}/test-agent/conversations`,
      conversation
    );
    return response.data.data;
  }

  // 批量创建对话
  async createTestAgentTurnsBatch(tenantCode: string, session_turns: Omit<TestAgentTurn, 'id'>[]): Promise<TestAgentTurn[]> {
    const response = await api.post(
      `/${tenantCode}/test-agent/conversations/batch`,
      { session_turns }
    );
    return response.data.data;
  }

  // 获取测试套件的对话列表
  async getTestAgentTurns(tenantCode: string, suiteId: number): Promise<TestAgentTurn[]> {
    const response = await api.get(
      `/${tenantCode}/test-agent/suites/${suiteId}/conversations`
    );
    return response.data.data;
  }

  // 更新对话
  async updateTestAgentTurn(tenantCode: string, id: number, updates: Partial<TestAgentTurn>): Promise<void> {
    await api.put(
      `/${tenantCode}/test-agent/conversations/${id}`,
      updates
    );
  }

  // 删除对话
  async deleteTestAgentTurn(tenantCode: string, id: number): Promise<void> {
    await api.delete(
      `/${tenantCode}/test-agent/conversations/${id}`
    );
  }

  // ==================== 预期答案 API ====================

  // 创建预期答案
  async createExpectedAnswer(tenantCode: string, answer: Omit<ExpectedAnswer, 'id'>): Promise<ExpectedAnswer> {
    const response = await api.post(
      `/${tenantCode}/test-agent/expected-answers`,
      answer
    );
    return response.data.data;
  }

  // 批量创建预期答案
  async createExpectedAnswersBatch(tenantCode: string, answers: Omit<ExpectedAnswer, 'id'>[]): Promise<ExpectedAnswer[]> {
    const response = await api.post(
      `/${tenantCode}/test-agent/expected-answers/batch`,
      { answers }
    );
    return response.data.data;
  }

  // 获取对话的预期答案列表
  async getExpectedAnswers(tenantCode: string, conversationId: number): Promise<ExpectedAnswer[]> {
    const response = await api.get(
      `/${tenantCode}/test-agent/conversations/${conversationId}/expected-answers`
    );
    return response.data.data;
  }

  // 更新预期答案
  async updateExpectedAnswer(tenantCode: string, id: number, updates: Partial<ExpectedAnswer>): Promise<void> {
    await api.put(
      `/${tenantCode}/test-agent/expected-answers/${id}`,
      updates
    );
  }

  // 删除预期答案
  async deleteExpectedAnswer(tenantCode: string, id: number): Promise<void> {
    await api.delete(
      `/${tenantCode}/test-agent/expected-answers/${id}`
    );
  }

  // ==================== 测试执行 API ====================

  // 获取测试套件的执行记录
  async getTestAgentExecutions(tenantCode: string, suiteId: number): Promise<TestAgentExecution[]> {
    const response = await api.get(
      `/${tenantCode}/test-agent/suites/${suiteId}/executions`
    );
    return response.data.data;
  }

  // ==================== 测试执行功能 ====================

  // 执行测试套件
  async runTestAgentSuite(
    tenantCode: string, 
    suiteId: number, 
    options?: {
      conversation_id?: string;
      platform_id?: number;
    }
  ): Promise<{
    execution: TestAgentExecution;
    results: TestAgentResult[];
    statistics: TestStatistics;
  }> {
    const response = await api.post(
      `/${tenantCode}/test-agent/suites/${suiteId}/run`,
      options || {},
      {
        timeout: 30 * 60 * 1000, // 30分钟超时，因为测试套件执行可能需要较长时间
      }
    );
    return {
      execution: response.data.data,
      results: response.data.data.results || [],
      statistics: response.data.data.statistics || {
        total_results: 0,
        matched_count: 0,
        mismatched_count: 0,
        partial_count: 0,
        average_score: 0
      }
    };
  }

  // ==================== 测试结果 API ====================

  // 创建测试结果
  async createTestAgentResult(tenantCode: string, result: Omit<TestAgentResult, 'id' | 'created_at'>): Promise<TestAgentResult> {
    const response = await api.post(
      `/${tenantCode}/test-agent/results`,
      result
    );
    return response.data.data;
  }

  // 获取测试执行的结果
  async getTestAgentResults(tenantCode: string, runId: number): Promise<TestAgentResult[]> {
    const response = await api.get(
      `/${tenantCode}/test-agent/executions/${runId}/results`
    );
    return response.data.data;
  }

  // 获取测试执行的统计信息
  async getTestAgentStatistics(tenantCode: string, runId: number): Promise<TestStatistics> {
    const response = await api.get(
      `/${tenantCode}/test-agent/executions/${runId}/statistics`
    );
    return response.data.data;
  }
}

// 导出单例
export const testAgentService = new TestAgentService();

